<script lang="ts">
import Button1XP from '../Button1XP/Button1XP.vue';
import Modal from './components/Modal/Modal.vue';
import Select from '../Select/Select.vue';
import Tag from '../Tag/Tag.vue';
import { onMounted, onUpdated, ref, watch, computed } from 'vue';
import { useStoreRegion } from '../../store/region/region';
import type { CountryOption, RegionType } from '../../types/region';
import { useStoreTranslations } from '../../store/translations/translations.ts';
import type { Instance } from '../../types/window.ts';

type Props = {
  corporateUrl: string;
  currentCountry: string;
  currentLanguage: string;
  currentLanguageCode: string;
  instance: Instance;
  isCorporateEN?: boolean;
};
</script>

<script setup lang="ts">
const { corporateUrl, currentCountry, currentLanguage, currentLanguageCode, isCorporateEN } =
  defineProps<Props>();

const storeRegions = useStoreRegion();
const storeTranslations = useStoreTranslations();

const isModalOpen = ref(false);
const isLoading = ref(true);
const countriesData = ref<RegionType['regions'][number]['countries']>([]);
const selectOptionsCountries = ref<CountryOption[]>([]);
const initCountry = ref();
const selectedCountry = ref();
const selectedLanguage = ref(currentLanguageCode);
const currentCountryRef = ref<string | undefined>(undefined);

const regions = computed(() => {
  return storeRegions.regions;
});
const worldwideData = {
  icon: 'worldwide',
  languages: [],
  name: 'WIKA Worldwide',
};

const handleOpen = () => {
  isModalOpen.value = true;
};

const handleClose = () => {
  isModalOpen.value = false;
};

const handleCancel = () => {
  isModalOpen.value = false;
  selectedCountry.value = initCountry.value;
  selectedLanguage.value = currentLanguageCode;
};

const handleSaveAndContinue = () => {
  const url =
    countriesData.value
      .find((country) => country.name === selectedCountry.value.name)
      ?.languages.find((language) => language.code === selectedLanguage.value)?.url ?? corporateUrl;

  openUrl(url);
};

const handleSelectCountry = (value) => {
  selectedCountry.value = value;

  if (selectedCountry.value.languages.length === 1) {
    selectedLanguage.value = selectedCountry.value.languages[0].code;
  } else {
    selectedLanguage.value = currentLanguageCode;
  }
};

const handleSelectLanguage = (languageCode: string) => {
  selectedLanguage.value = languageCode;
};

const openUrl = (url) => {
  window.location.href = url;
};

const initApiData = () => {
  if (regions.value?.items?.regions) {
    const regionsData = regions.value?.items.regions;
    const countrySelectOptionsRaw: CountryOption[] = [];

    for (let region of regionsData) {
      for (let country of region.countries) {
        if (country.flag && country.languages && country.languages.length !== 0) {
          countrySelectOptionsRaw.push({
            icon: country.flag,
            languages: country.languages.map((language) => ({
              code: language.code,
              title: language.title,
            })),
            name: country.name,
          });

          if (country.name === currentCountry) {
            const currentCountryData = {
              icon: country.flag,
              languages: country.languages.map((language) => ({
                code: language.code,
                title: language.title,
              })),
              name: country.name,
            };

            initCountry.value = {
              ...currentCountryData,
            };

            selectedCountry.value = {
              ...currentCountryData,
            };
          }
        }

        countriesData.value.push(...region.countries);
      }
    }

    selectOptionsCountries.value = countrySelectOptionsRaw.sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    isLoading.value = false;
  }
};

const detectCurrentCountry = () => {
  if (isCorporateEN) {
    currentCountryRef.value =
      storeTranslations.translations?.['countryLanguageSwitcher.corporate.countryLabel'] ||
      'Worldwide';
  } else {
    currentCountryRef.value = currentCountry;
  }
};

watch(
  () => storeRegions,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue) {
      initApiData();
    }
  },
  { immediate: true, deep: true },
);

watch(
  regions,
  (regionsItemsNew, regionsItemsOld) => {
    if (regionsItemsNew !== regionsItemsOld && regionsItemsNew && regionsItemsNew.items) {
      initApiData();
    }
  },
  { immediate: true, deep: true },
);

onMounted(() => {
  detectCurrentCountry();
});

onUpdated(() => {
  detectCurrentCountry();
});
</script>

<template>
  <div v-show="!isLoading && selectOptionsCountries.length > 0" :class="$style.container">
    <Button1XP
      end-icon="keyboard_arrow_down"
      size="s"
      start-icon="location_on"
      start-icon-size="large"
      :text="`${currentCountryRef} | ${currentLanguage.toUpperCase()}`"
      variant="linkStatic"
      @click="handleOpen"
    />
    <Modal v-if="isModalOpen">
      <div :class="$style.modalContainer">
        <div :class="$style.close">
          <Button1XP size="xs" start-icon="close" variant="ghost" @click="handleClose" />
        </div>
        <span :class="$style.title">
          {{ storeTranslations.translations?.['countryLanguageSwitcher.title'] }}
        </span>
        <Button1XP
          :class="$style.subtitle"
          :text="
            storeTranslations.translations?.[
              instance === 'shop'
                ? 'shop.countryLanguageSwitcher.subtitle'
                : 'countryLanguageSwitcher.subtitle'
            ]
          "
          :url="corporateUrl"
          variant="linkStatic"
        />
        <div :class="$style.countrySelectorContainer">
          <span>{{ storeTranslations.translations?.['countryLanguageSwitcher.country'] }}</span>
          <div :class="$style.countrySelector">
            <Select
              colour="oneXP"
              label="name"
              :placeholder="
                storeTranslations.translations?.['countryLanguageSwitcher.countrySelectPlaceholder']
              "
              :searchable="true"
              :select-options="[worldwideData, ...selectOptionsCountries]"
              :select-value="selectedCountry"
              track-by="name"
              @on-change="handleSelectCountry"
            />
          </div>
        </div>

        <div :class="$style.languageContainer">
          <template v-if="selectedCountry?.languages.length > 0">
            <span>{{ storeTranslations.translations?.['countryLanguageSwitcher.language'] }}</span>
            <div :class="$style.languageContainerButtons">
              <div v-for="language in selectedCountry?.languages" :key="language">
                <Tag
                  :is-active="selectedLanguage === language.code"
                  :text="language.title"
                  @click="handleSelectLanguage(language.code)"
                />
              </div>
            </div>
          </template>
        </div>

        <div :class="$style.modalButtons">
          <Button1XP
            :class="$style.modalButton"
            size="s"
            :text="storeTranslations.translations?.['countryLanguageSwitcher.saveButtonText']"
            variant="primary"
            @click="handleSaveAndContinue"
          />
          <Button1XP
            :class="$style.modalButton"
            size="s"
            :text="storeTranslations.translations?.['countryLanguageSwitcher.cancelButtonText']"
            variant="ghost"
            @click="handleCancel"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>

<style module lang="scss" src="./CountryLanguageSwitcher1XP.scss" />
